import { useEffect, useRef, useState } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import clsx from 'clsx';

import Button from '../Button';
import Icon from '../Icon';
import { usePopupPosition } from '@hooks';
import { useForm, FormItem } from '../Form';
import { formatDate } from '../Daypicker/utils';
import { RangeDayPicker } from '../Daypicker/RangeDayPicker';
import { ISO_FORMAT, getDate } from '@utilities';
import type { DateRangeValue } from '../Daypicker/utils';

export type DateStringValue = [string | undefined, string | undefined];

type FilterValue = Date | string | null | undefined;

export type DateFilterV2Props<T> = {
  toName: StringKeyOf<T>;
  fromName: StringKeyOf<T>;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

const parseDate = (value: FilterValue): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;

  const nativeDate = new Date(value);
  if (!Number.isNaN(nativeDate.getTime())) return nativeDate;

  return getDate(value, ISO_FORMAT) ?? undefined;
};

const toStringValue = (range: DateRangeValue): DateStringValue => {
  const fromStr = range[0] ? range[0].toISOString() : undefined;
  const toStr = range[1] ? range[1].toISOString() : undefined;
  return [fromStr, toStr];
};

const toDateRange = (value?: [FilterValue, FilterValue]): DateRangeValue => [
  parseDate(value?.[0]),
  parseDate(value?.[1]),
];

const hasShowableValue = (value: FilterValue) =>
  value !== undefined && value !== null;

export const DateFilterV2 = <T extends {} = any>({
  toName,
  fromName,
  required,
  placeholder = 'Select date range',
  disabled = false,
}: DateFilterV2Props<T>) => {
  const { getValue, onChange, getErrors } = useForm<any>();

  const formValues = getValue?.();
  const from = formValues?.[fromName];
  const to = formValues?.[toName];

  const errors = getErrors?.() ?? {};
  const fromError = errors[fromName];
  const toError = errors[toName];

  const isErrorState =
    (hasShowableValue(from) && !!fromError) ||
    (hasShowableValue(to) && !!toError);

  const [open, setOpen] = useState(false);
  const refAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const { popupRef, anchorRef, popupPosition } = usePopupPosition({
    enable: open,
  });

  useOnClickOutside(refAnchorRef, () => setOpen(false));

  const rangeValue = toDateRange([from, to]);
  const hasValue = Boolean(rangeValue[0] || rangeValue[1]);

  const displayText = hasValue
    ? `${formatDate(rangeValue[0])} - ${formatDate(rangeValue[1])}`
    : placeholder;

  const handleChange = (range: DateRangeValue) => {
    const [fromValue, toValue] = toStringValue(range);

    onChange?.({
      ...getValue?.(),
      [fromName]: fromValue,
      [toName]: toValue,
    });

    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <div
      ref={node => {
        node && anchorRef(node);
        refAnchorRef.current = node;
      }}
      className="relative"
    >
      {/* No UI, just subscribe */}
      <FormItem<any> name={fromName} label="" required={required} />
      <FormItem<any> name={toName} label="" required={required} />

      <DateInput
        open={open}
        error={isErrorState}
        hasValue={hasValue}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        {displayText}
      </DateInput>

      {open && !disabled && (
        <div
          ref={popupRef}
          style={{
            top: popupPosition?.style?.top ?? 0,
            left: popupPosition?.style?.left ?? 0,
          }}
          className="fixed z-popper py-sm"
        >
          <RangeDayPicker
            value={rangeValue}
            onChange={handleChange}
            onCancel={handleCancel}
          />
        </div>
      )}
    </div>
  );
};

type DateInputProps = {
  open: boolean;
  error: boolean;
  hasValue: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
};

const DateInput: React.FC<DateInputProps> = props => {
  const { open, error, hasValue, disabled = false, children, onClick } = props;

  return (
    <Button
      type="light"
      width="small"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'px-md py-md max-h-[3.6rem] shadow-none bg-transparent font-normal hover:bg-transparent',
        error && 'border-danger hover:border-danger',
        !error && open && 'border-main hover:border-main',
        !error && !open && 'hover:border-main'
      )}
    >
      <div className="flex items-center justify-center gap-sm">
        <Icon
          icon="calendar"
          className={clsx(
            'shrink-0 transition-default',
            open ? 'text-main' : 'text-textLight'
          )}
        />
        <span
          className={clsx(
            'leading-none',
            hasValue ? 'text-text' : 'text-textLight'
          )}
        >
          {children}
        </span>
      </div>
    </Button>
  );
};
